import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Row, Col } from 'react-bootstrap'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useTranslationRankmi } from 'helpers/translations.helper'
import { sortTableEnum } from 'enum/sortTable.enum'
import {
  DOWNLOAD_ALL_COURSES_MONITORING,
  TDataDownloadAllCoursesMonitoring,
  MutationDownloadAllCoursesMonitoringArgs,
  ExternalCourse,
  Course,
  CourseParticipantRansack,
} from 'graphqlAPI'
import { downloadFile } from 'helpers/downloadFile.helper'
import { ChildFilterTypes } from 'enum'
import CustomDropdown from 'components/ui/CustomDropdown'
import { DropdownReport } from 'components/dropdowns/DropdownReport'
import { prefix } from './constants'
import styles from './Courses.module.scss'
import Table from './table'

type Data = Course | ExternalCourse

interface CoursesTabProps {
  areaId?: string
  nodes: Data[]
  total: number
  page: number
  loading: boolean
  setText: (text: string) => void
  setPerPage: (perPage: number) => void
  setSorts: (sorts: string[]) => void
  setPage: (page: number) => void
  type: ChildFilterTypes
  participantsQuery?: CourseParticipantRansack
  onDownloadReport?: (selected: number[], allSelected: boolean) => void
}

export const CoursesTab = ({
  areaId,
  nodes,
  total,
  loading,
  page,
  setPage,
  setText,
  setSorts,
  setPerPage,
  onDownloadReport,
  type,
  participantsQuery,
}: CoursesTabProps) => {
  const tRelative = useTranslationRankmi({ prefix })

  const [selected, setSelected] = useState<string[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const [downloadAllCoursesMonitoring] = useMutation<
    TDataDownloadAllCoursesMonitoring,
    MutationDownloadAllCoursesMonitoringArgs
  >(DOWNLOAD_ALL_COURSES_MONITORING)

  const ids = useMemo(() => {
    return selected.reduce<Record<Required<Data>['__typename'], number[]>>(
      (acc, item) => {
        if (typeof item === 'string') {
          const [id, __typename] = item.split('-')
          if (__typename && acc[__typename]) {
            acc[__typename].push(+id)
          }
        }
        return acc
      },
      {
        Course: [],
        ExternalCourse: [],
      }
    )
  }, [selected])

  const isDownloadDisabled = !allSelected && !selected.length
  return (
    <Row>
      <Col>
        <CustomDropdown
          id="dropdown-manage"
          className={`m-2 ${styles.dropdownManage}`}
          alignRight
        >
          <CustomDropdown.Toggle
            id="dropdown-manage-toggle"
            bsPrefix="noCaret"
            variant={'secondary'}
          >
            {tRelative('actions.label')}{' '}
            <FontAwesomeIcon icon={faChevronDown} />
          </CustomDropdown.Toggle>
          <CustomDropdown.Menu>
            {onDownloadReport && (
              <DropdownReport
                disabled={isDownloadDisabled}
                onClick={() =>
                  onDownloadReport(
                    selected.map((id: string) =>
                      Number((id.match(/^\d+/) || [0])[0])
                    ),
                    allSelected
                  )
                }
              >
                {tRelative('actions.options.downloadCourseReportComplete', {
                  count: !allSelected
                    ? selected.length
                    : total - selected.length,
                })}
              </DropdownReport>
            )}
            <DropdownReport
              disabled={isDownloadDisabled}
              onClick={async () => {
                const none = [0]
                const buildQuery = (ids: number[]) => ({
                  id_in: allSelected ? [] : ids.length ? ids : none,
                  ...(allSelected && { id_not_in: ids }),
                })
                const { url } = await downloadAllCoursesMonitoring({
                  variables: {
                    input: {
                      areaId,
                      participantsQuery,
                      qCourses: (type === ChildFilterTypes.Course &&
                        buildQuery(ids.Course)) || { id_in: none },
                      qExternalCourses: (type ===
                        ChildFilterTypes.ExternalCourse &&
                        buildQuery(ids.ExternalCourse)) || { id_in: none },
                      perPage: 1000,
                    },
                  },
                }).then(({ data }) => ({
                  ...data?.downloadAllCoursesMonitoring,
                }))
                if (url) downloadFile(url)
              }}
            >
              {tRelative('actions.options.downloadCourseReports', {
                count: !allSelected ? selected.length : total - selected.length,
              })}
            </DropdownReport>
          </CustomDropdown.Menu>
        </CustomDropdown>
      </Col>
      <Col xs={12}>
        <Table
          setPage={(newPage: number) => {
            setPage(newPage)
          }}
          setSearch={(newSearch: string) => {
            setText(newSearch)
          }}
          setSort={({ field, order }) => {
            setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
          }}
          setPerPage={(newPerPage: number) => {
            setPerPage(newPerPage)
          }}
          setSelectedRows={(newSelectedRows: string[]) => {
            setSelected(newSelectedRows)
          }}
          selectedRows={selected}
          setAllSelected={(newAllSelected: boolean) => {
            setAllSelected(newAllSelected)
          }}
          loading={loading}
          list={nodes}
          total={total}
          currentPage={page}
        />
      </Col>
    </Row>
  )
}
